import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, unwrapLookupItems,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/productrestrictions';

const DOC_TYPE_OPTIONS = [
  { value: '', label: 'Hepsi' },
  { value: 1, label: 'Sipariş' },
];
const docTypeLabel = (value) => {
  if (value === null || value === undefined || value === '') return 'Hepsi';
  const found = DOC_TYPE_OPTIONS.find((o) => o.value === value);
  return found ? found.label : String(value);
};

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  description: { type: 'text', odataField: 'Description' },
  documentType: { type: 'select', odataField: 'DocumentType', options: [{ value: 1, label: 'Sipariş' }] },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }] },
};
const SEARCH_FIELDS = ['TextId', 'Description'];

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

export function ProductRestrictionsPage() {
  usePageMeta('Ürün Kısıtlama', 'Belge tipi ve müşteri kriterleri bazında ürünleri satışa kapatın.');

  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerSubGroups, setCustomerSubGroups] = useState([]);
  const [customerLabels, setCustomerLabels] = useState({}); // id -> label (lazy)

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: görünür kolonların OData alanları + kritikler. "Müşteri Kriter" display kolonu
  // ve müşteri etiketi effect'i row'dan CustomerGroupId/CustomerSubGroupId/CustomerId okur
  // (COLUMN_FILTER_META'da yok) → always'e ekli, yoksa kriter kolonu boş kalır.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'CustomerGroupId', 'CustomerSubGroupId', 'CustomerId'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'TextId',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customergroups/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/customersubgroups/Select', { headers }).then((res) => res.ok ? res.json() : []),
    ]).then(([cg, csg]) => {
      setCustomerGroups(unwrapLookupItems(cg));
      setCustomerSubGroups(unwrapLookupItems(csg));
    }).catch(() => {});
  }, []);

  const customerGroupName = useMemo(() => {
    const map = new Map(customerGroups.map((g) => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [customerGroups]);
  const customerSubGroupName = useMemo(() => {
    const map = new Map(customerSubGroups.map((g) => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [customerSubGroups]);
  const customerName = useCallback((id) => (id ? (customerLabels[id] || '…') : ''), [customerLabels]);

  // Müşteri etiketlerini id bazında lazy çek (4000+ müşteri → tümü yüklenmez).
  const custFetchRef = useRef(new Set());
  const ensureCustomerLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((id) => id && customerLabels[id] === undefined && !custFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => custFetchRef.current.add(id));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `/api/customers?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) { const data = await res.json(); getODataRows(data).forEach((c) => { found[c.id] = lookupLabel(c); }); }
        setCustomerLabels((prev) => { const next = { ...prev }; chunk.forEach((id) => { next[id] = found[id] || '-'; }); return next; });
      }
    } finally {
      need.forEach((id) => custFetchRef.current.delete(id));
    }
  }, [customerLabels]);

  useEffect(() => {
    if (rows.length) ensureCustomerLabels(rows.map((r) => r.customerId).filter(Boolean));
  }, [rows, ensureCustomerLabels]);

  const openNew = () => navigate('/product-restrictions/new');
  const openEdit = (row = selected) => { if (row) navigate(`/product-restrictions/${row.id}?edit=1`, { state: { row } }); };
  const openView = (row = selected) => { if (row) navigate(`/product-restrictions/${row.id}`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Ürün kısıtlaması başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('description', { header: 'Açıklama', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('documentType', { header: 'Belge Tipi', meta: { filterType: 'select' }, cell: (info) => docTypeLabel(info.getValue()) }),
    columnHelper.display({
      id: 'criteria', header: 'Müşteri Kriter',
      cell: ({ row }) => {
        const parts = [];
        const cg = customerGroupName(row.original.customerGroupId);
        if (cg) parts.push(`Grup: ${cg}`);
        const csg = customerSubGroupName(row.original.customerSubGroupId);
        if (csg) parts.push(`EkGrup: ${csg}`);
        const c = customerName(row.original.customerId);
        if (c) parts.push(`Müşteri: ${c}`);
        return parts.length === 0 ? 'Tüm müşteriler' : parts.join(' / ');
      },
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() !== false ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="bad">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [customerGroupName, customerSubGroupName, customerName, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('TextId', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Kod' },
        { field: 'description', headerName: 'Açıklama' },
        { headerName: 'Belge Tipi', valueGetter: ({ row }) => docTypeLabel(row.documentType) },
        { headerName: 'Müşteri Grubu', valueGetter: ({ row }) => customerGroupName(row.customerGroupId) },
        { headerName: 'Müşteri Ek Grubu', valueGetter: ({ row }) => customerSubGroupName(row.customerSubGroupId) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => customerName(row.customerId) },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive !== false ? 'Aktif' : 'Pasif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `urun-kisitlamalari_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Ürün Kısıtlama"
        subtitle="Belge tipi ve müşteri kriterleri bazında ürünleri satışa kapatın."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Kısıtlama</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openView}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected.textId}"</strong> kısıtlamasını silmek istediğinize emin misiniz? Bağlı tüm ürün satırları da silinecek.</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
